import {
  Component,
  computed,
  inject,
  QueryList,
  Signal,
  signal,
  TemplateRef,
  ViewChild,
  ViewChildren
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { MatMenuModule } from '@angular/material/menu';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { BreakpointObserver } from '@angular/cdk/layout';
import { ButtonComponent } from '@shared/components/atoms/button/button.component';
import { OKTA_AUTH } from '@okta/okta-angular';
import { CommonModule } from '@angular/common';
import { UserStore } from '@shared/stores/user.store';
import { NMFTAIconComponent } from '@shared/components/atoms/icon/icon.component';
import { DialogComponent } from '../dialog/dialog.component';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { MatAccordion, MatExpansionModule, MatExpansionPanel } from '@angular/material/expansion';
import { filter } from 'rxjs';
import { MatRipple } from '@angular/material/core';
import { SearchBarComponent } from '@shared/components/organisms/search-bar/search-bar.component';
import { StringConstants } from '@shared/constants/strings.constants';
import { NewsFeedStore } from '@features/admin/store/news-feed.store';
import { Newsfeed } from '@features/admin/models/newsfeed.model';

@Component({
    selector: 'nmfta-nav-bar',
    imports: [
        RouterModule,
        TranslateModule,
        MatButtonModule,
        MatMenuModule,
        MatIconModule,
        ButtonComponent,
        SearchBarComponent,
        CommonModule,
        NMFTAIconComponent,
        NgxSkeletonLoaderModule,
        MatAccordion,
        MatExpansionModule,
        MatRipple,
    ],
    templateUrl: './nav-bar.component.html',
    styleUrl: './nav-bar.component.scss'
})
export class NavBarComponent {
  userStore = inject(UserStore);
  oktaAuth = inject(OKTA_AUTH);
  matDialog = inject(MatDialog);
  private readonly router = inject(Router);
  readonly panelOpenState = signal(false);

  private readonly newsFeedStore = inject(NewsFeedStore);
  news$: Signal<Newsfeed[]> = this.newsFeedStore.activeBannerNews;
  displayingBanner$: Signal<boolean> = this.newsFeedStore.displayBanner;

  @ViewChild('nmfcBookModalTemplate', { static: true }) nmfcBookModalTemplate!: TemplateRef<unknown>;
  @ViewChildren(MatExpansionPanel) panels!: QueryList<MatExpansionPanel>;

  accountManager = StringConstants.role_AccountManager;

  langList = [
    { code: 'en', label: 'ENG', language: 'English' },
    { code: 'fr', label: 'FRE', language: 'French' },
    { code: 'es', label: 'ESP', language: 'Spanish' },
  ];

  langLabel: string;
  langSelected: string;
  isSmallScreen = false;
  user$ = this.userStore.user;
  isAuthenticated$ = this.userStore.isAuthenticated;
  featuresListMenu = [
    { path: 'search-history', label: 'Search History' },
    { path: 'favorites', label: 'Favorites' },
    { path: 'synonyms', label: 'Synonyms' },
    { path: 'company-common', label: 'Company Common' },
    { path: 'comments', label: 'Comments' },
    { path: 'nmfc-book', label: 'NMFC Book' },
  ];

  dialogRef: MatDialogRef<any>;
  dialogConfig: any = {};

  isOpen = false;
  private currentUrl$ = signal(this.router.url);


  readonly userLoading$ = this.userStore.userLoading;
  readonly isOktaLoginCallback$ = computed(() => {
    const url = this.currentUrl$();
    return url === '/' || url.startsWith('/login/callback');
  });


  constructor(
    private translate: TranslateService,
    private breakpointObserver: BreakpointObserver,
  ) {
    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((e) => {
        this.currentUrl$.set(e.urlAfterRedirects);
      });
    this.langSelected = this.translate.getDefaultLang();
    this.langLabel = this.langList.find((lang) => lang.code === this.langSelected)!.label;
    this.breakpointObserver.observe('(min-width: 992px)').subscribe((result) => {
      this.isSmallScreen = !result.matches;
      if (result.matches && this.isOpen) {
        this.closeAccordions();
        this.toggleNavMenu();
      }
    });
  }

  closeAccordions() {
    this.panels.forEach((panel) => {
      if (panel.expanded) {
        panel.close();
      }
    });
  }

  toggleNavMenu() {
    this.closeAccordions();
    this.isOpen = !this.isOpen;
  }

  useLanguage(code: string): void {
    this.langLabel = this.langList.find((lang) => lang.code === code)!.label;
    this.langSelected = code;
    this.translate.use(code);
  }

  public async signIn(): Promise<void> {
    await this.oktaAuth.signInWithRedirect();
  }

  public async signOut(): Promise<void> {
    await this.oktaAuth.signOut();
  }

  public nmfcBookMessage() {
    this.dialogConfig = {
      width: '46.5rem',
      maxWidth: '46.5rem',
      height: '23rem',
      data: {
        title: this.translate.instant('Generic_Purchase') + ' ' + this.translate.instant('Generic_NMFCBook'),
        icon: 'description',
        content: this.nmfcBookModalTemplate,
        actions: [
          {
            id: 'cancel',
            label: 'Generic_Cancel',
            styleType: 'contained',
            color: 'light',
            onClick: (): void => this.dialogRef.close(),
          },
          {
            id: 'storelink',
            label: this.translate.instant('Generic_OK'),
            styleType: 'contained',
            color: 'accent',
            onClick: (): void => this.dialogRef.close(),
          },
        ],
      },
    };
    this.dialogRef = this.matDialog.open(DialogComponent, this.dialogConfig);
  }

  public routeToExternalLink(url: string): void {
    if (typeof window !== 'undefined') window.open(url, '_blank');
  }

  navigate(route: string) {
    if (this.isSmallScreen) {
      this.closeAccordions();
      this.toggleNavMenu();
    }
    this.router.navigate([route]);
  }

  showBanner() {
    if (this.isSmallScreen) {
      this.closeAccordions();
      this.toggleNavMenu();
    }
    this.newsFeedStore.toggleDisplayBanner(true);
  }
}
